import { useLayoutEffect, useRef, useState, useCallback } from 'react';
import { SxProps, Theme } from '@liquidweb-dev/undercurrent/styles';

export interface AutoWidthOptions {
	/** Minimum width in pixels @default 200 */
	minWidth?: number;
	/** Maximum width in pixels @default 400 */
	maxWidth?: number;
	/** Width for non-text elements (icons, buttons, padding) @default 120 */
	chromeWidth?: number;
}

interface UseAutoWidthParams<T> {
	/** Current value to measure */
	value: T;
	/** Enable auto-width or pass options */
	autoWidth: boolean | AutoWidthOptions;
	/** Placeholder text (used when value is empty) */
	placeholder?: string;
	/** Label text (fallback when placeholder is empty) */
	label?: string;
	/** Function to get display text from value */
	getOptionLabel?: (value: T) => string;
}

interface UseAutoWidthReturn {
	/** Ref for the hidden span used to measure text width */
	textMeasureRef: React.RefObject<HTMLSpanElement>;
	/** Ref for the input container to measure chrome width */
	inputRef: React.RefObject<HTMLDivElement>;
	/** Calculated width in pixels */
	width: number;
	/** Sx prop with auto-width styles (undefined if disabled) */
	sx: SxProps<Theme> | undefined;
	/** Whether auto-width is enabled */
	isEnabled: boolean;
}

/**
 * Hook to dynamically size an input based on its content
 */
function useAutoWidth<T>({
	value,
	autoWidth,
	placeholder,
	label,
	getOptionLabel,
}: UseAutoWidthParams<T>): UseAutoWidthReturn {
	const isEnabled = Boolean(autoWidth);
	const options = typeof autoWidth === 'object' ? autoWidth : {};
	const { minWidth = 200, maxWidth = 400, chromeWidth = 120 } = options;

	const textMeasureRef = useRef<HTMLSpanElement>(null);
	const inputRef = useRef<HTMLDivElement>(null);
	const [calculatedWidth, setCalculatedWidth] = useState(minWidth);

	const getDisplayText = useCallback(
		(val: T): string => {
			if (!val) return placeholder || label || '';
			if (getOptionLabel) return getOptionLabel(val);
			if (typeof val === 'string') return val;
			if (typeof val === 'object' && val !== null && 'label' in val) {
				return (val as { label: string }).label;
			}
			return '';
		},
		[placeholder, label, getOptionLabel],
	);

	useLayoutEffect(() => {
		if (!isEnabled || !textMeasureRef.current) return;

		const textToMeasure = getDisplayText(value);
		textMeasureRef.current.textContent = textToMeasure;
		const textWidth = textMeasureRef.current.offsetWidth;

		const measuredWidth = textWidth + chromeWidth;
		const clampedWidth = Math.min(Math.max(measuredWidth, minWidth), maxWidth);
		setCalculatedWidth(clampedWidth);
	}, [value, isEnabled, minWidth, maxWidth, chromeWidth, getDisplayText]);

	const sx: SxProps<Theme> | undefined = isEnabled
		? {
				width: { xs: '100%', sm: calculatedWidth },
				maxWidth: { xs: '100%', sm: maxWidth },
				transition: 'width 0.15s ease-out',
			}
		: undefined;

	return {
		textMeasureRef,
		inputRef,
		width: calculatedWidth,
		sx,
		isEnabled,
	};
}

export default useAutoWidth;
